import { useEffect, useState, useTransition } from "react";
import styles from "../aic.module.css";
import { AioLabel } from "./aioLabel";

interface AioNumberProps {
  id: string;
  value: number;
  label?: string;
  setValue?: (value: number) => void;
  step?: number;
  minValue?: number;
  maxValue?: number;
}

/**
 * A number input component with label and optional constraints.
 *
 * @param props - The component props for the aioNumber component
 * @param props.id - The id for the input element
 * @param props.value - The current numeric value
 * @param props.label - Optional label for the input
 * @param props.setValue - Optional function to update the numeric value
 * @param props.step - Optional step value for the input
 * @param props.minValue - Optional minimum value constraint
 * @param props.maxValue - Optional maximum value constraint
 */
export const AioNumber = (props: AioNumberProps): React.ReactElement => {
  const [_isPending, startTransition] = useTransition();
  const [value, setValue] = useState<number>(props.value);
  useEffect(() => {
    startTransition(() =>
      setValue(
        Math.max(props.minValue ?? -Infinity, Math.min(props.maxValue ?? Infinity, props.value)),
      ),
    );
  }, [props.maxValue, props.minValue, props.value]);

  return (
    <>
      <AioLabel
        id={`${props.id}-label`}
        label={props.label}
        htmlFor={props.id}
      />
      <div className={styles.aioInputHolder}>
        <input
          id={props.id}
          className={styles.aioInput}
          aria-labelledby={`${props.id}-label`}
          value={`${value}`}
          type={"number"}
          disabled={!props.setValue}
          onChange={(e) => setValue(parseFloat(e.currentTarget.value))}
          onBlur={() => props.setValue?.(value)}
          step={props.step}
          min={props.minValue}
          max={props.maxValue}
        />
      </div>
    </>
  );
};

AioNumber.displayName = "AioNumber";
